import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useSnackbar } from 'providers/snackbar';
import { FC, ReactNode, useMemo } from 'react';

const VITE_GRAPH_API = import.meta.env.VITE_GRAPH_API;

const DEFAULT_DEV_URI = 'https://internal.dev.aktify.io/graphql';

const httpLink = createHttpLink({
  uri: typeof VITE_GRAPH_API !== 'string' ? DEFAULT_DEV_URI : VITE_GRAPH_API,
});

const authLink = setContext(async (_, { headers }) => {
  try {
    const session = await fetchAuthSession();

    if (!session) {
      return { headers };
    }
    const token = session.tokens?.accessToken || '';

    return {
      headers: {
        ...headers,
        authorization: token ?? '',
      },
    };
  } catch (e) {
    return { headers };
  }
});

const ErrorParagraph: FC<{ boldedPrefix?: string; message: string | Record<string, any> }> = ({
  boldedPrefix,
  message,
}) => {
  if (!message) {
    return null;
  }
  message = typeof message == 'string' ? message : JSON.stringify(message);
  return (
    <p>
      {boldedPrefix ? <b>{boldedPrefix}:</b> : ''} {message}
    </p>
  );
};

const createErrorMessage = (errorResponse: ErrorResponse): null | ReactNode => {
  const { networkError, graphQLErrors } = errorResponse;
  if (networkError) {
    console.error(networkError);
    return <ErrorParagraph boldedPrefix="Network error" message={networkError.message} />;
  }

  if (graphQLErrors) {
    graphQLErrors.forEach((e) => {
      console.error(`[GraphQL error]: ${e.message}`);
      console.warn(e);
    });

    return (
      <div>
        {graphQLErrors.map(({ message, extensions }, index) => {
          const errorText = graphQLErrors.length > 1 ? `Error ${index + 1}` : 'Error';
          const responseMessage = extensions?.response?.message;
          const errorMessage = extensions?.response?.body?.errors?.[0]?.message;
          return (
            <div key={index}>
              <ErrorParagraph boldedPrefix={errorText} message={message} />
              <ErrorParagraph message={responseMessage} />
              <ErrorParagraph message={errorMessage} />
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

export const GraphqlWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const { openSnackbar } = useSnackbar();

  const client = useMemo(
    () =>
      new ApolloClient({
        link: ApolloLink.from([
          onError((errorResponse) => {
            const errorMessage = createErrorMessage(errorResponse);
            if (errorMessage) {
              openSnackbar({
                message: (
                  <div>
                    <h2>Something went wrong &#128577;</h2>
                    {errorMessage}
                  </div>
                ),
                severity: 'error',
                duration: undefined,
                closeable: true,
              });
            }
          }),
          authLink.concat(httpLink),
        ]),
        cache: new InMemoryCache({
          typePolicies: {
            AssignedCalendar: {
              keyFields: ['id', 'teamMemberId'],
            },
            DncLead: {
              keyFields: ['id', 'phone', 'created'],
            },
            // A guardrail can appear in both `inboundGuardrails` and `outboundGuardrails`
            // of the same LLM config with different `isActive` / `respondWith` values.
            // Default normalization by `id` would collapse them into one cached entity
            // and the second-written copy would clobber the first.
            GuardrailItem: { keyFields: false },
          },
        }),
        defaultOptions: {
          watchQuery: {
            nextFetchPolicy: 'cache-and-network',
            notifyOnNetworkStatusChange: true,
          },
          query: {
            notifyOnNetworkStatusChange: true,
          },
        },
        devtools: {
          enabled: true,
        },
      }),
    [],
  );
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};
